/**
 * One exercise outside the catalogue as board E draws it: the row (ring or check, the name or what
 * happened to it, „Do decyzji" or „Zmień", the chevron) and, while it is open, „Propozycja dodania
 * nowego ćwiczenia" folded under it — the proposal's four facts with „Szybkie dodanie" and
 * „Zweryfikuj i dodaj", the similar exercises one click each, the catalogue search, „Pomiń".
 *
 * The written name is drawn in full, in the row's title, before anything can be added: a name read
 * off a spreadsheet could carry words meant for somebody else, and the trainer adds what they read.
 * It is text, as every name here is.
 *
 * Every decision goes through `DraftDecisions`; the row holds only the search it has open, and
 * whether focus is owed to its header: a decision made from the open panel folds it away with the
 * control that made it, so once that decision has LANDED focus goes to the header, which stands and
 * says what happened. A decision that failed, was refused or was cancelled (the creator closed
 * unsaved) owes nothing, and never steals focus later.
 */

import clsx from 'clsx';
import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { MAX_EXERCISE_NAME_LENGTH, useExerciseLibraryQuery } from '~/entities/exercise-library';
import { CheckGlyph } from '~/shared/ui/FormGlyphs';
import { SearchField } from '~/shared/ui/SearchField';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import { addFailureWords, outcomeWords, proposalFacts } from '../lib/outsideCatalogueWording';
import type { DraftDecisions } from '../lib/useExerciseDecisions';
import type { OutsideCatalogueRow } from '../model/outsideCatalogue';

import { ChevronGlyph, ErrorGlyph, SkippedGlyph } from './ChatGlyphs';
import surfaces from './surfaces.module.css';
import styles from './OutsideCatalogue.module.css';

/** Board E shows a handful of matches under the field; typing more narrows them. */
const SEARCH_RESULTS = 4;

interface ExerciseDecisionRowProps {
  readonly row: OutsideCatalogueRow;
  readonly decisions: DraftDecisions;
  /** Resolved before this screen drew it: it stands still rather than playing its check. */
  readonly quiet: boolean;
  /** Its place among the rows „Dodaj wszystkie szybko" resolves together, for the stagger. */
  readonly batchStep: number | null;
  /** The row is reopened from here: the list drops its stillness. */
  readonly onReopen: () => void;
  /**
   * Given for one of Buddy's own picks opened to change under „Założyłem": the row is always open
   * while it waits, and its header and „Zostaw wybór Buddy’ego" close it with nothing sent.
   */
  readonly onLeave?: () => void;
}

export function ExerciseDecisionRow({ row, decisions, quiet, batchStep, onReopen, onLeave }: ExerciseDecisionRowProps) {
  const { t } = useTranslation();
  const panelId = useId();
  const busyId = useId();
  const { exercise, resolved, outcome } = row;
  const name = exercise.name;
  const expanded = onLeave !== undefined ? !resolved : row.expanded;
  const busy = decisions.busy(name);
  const done = resolved && outcome !== null ? outcomeWords(t, exercise, outcome) : null;
  const header = useRef<HTMLButtonElement>(null);
  const owesFocus = useRef(false);
  useEffect(() => {
    if (!expanded && owesFocus.current) {
      owesFocus.current = false;
      header.current?.focus();
    }
  }, [expanded]);
  // A decision from the panel: focus is owed only if it lands.
  const act = (decision: Promise<boolean>) => {
    owesFocus.current = true;
    void decision.then((landed) => {
      if (!landed) owesFocus.current = false;
    });
  };

  return (
    <li
      className={clsx(styles.item, resolved && styles.resolvedRow, resolved && !quiet && styles.resolving)}
      style={batchStep === null ? undefined : ({ '--batch-step': String(batchStep) } as CSSProperties)}
    >
      <button
        ref={header}
        type="button"
        className={clsx(styles.row, expanded && styles.rowOpen)}
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => {
          if (resolved) {
            onReopen();
            decisions.reopen(name);
          } else if (onLeave !== undefined) {
            if (!busy) onLeave();
          } else {
            decisions.toggle(name);
          }
        }}
      >
        {resolved ? (
          <span className={clsx(styles.check, outcome?.kind === 'skipped' && styles.skipped)} aria-hidden="true">
            {outcome?.kind === 'skipped' ? (
              <SkippedGlyph className={styles.checkGlyph} />
            ) : (
              <CheckGlyph className={styles.checkGlyph} />
            )}
          </span>
        ) : (
          <span className={styles.ring} aria-hidden="true" />
        )}
        <span className={styles.name}>
          <span className={styles.nameTitle}>{done?.title ?? t('web:aiPlan.unresolved.name', { name })}</span>
          <span className={styles.nameSub}>{done?.sub ?? exercise.days.join(', ')}</span>
        </span>
        <span className={clsx(styles.trail, resolved && styles.trailChange)}>
          {resolved
            ? t('web:aiPlan.unresolved.change')
            : row.failure !== null
              ? t('web:aiPlan.unresolved.notAdded')
              : t('web:aiPlan.unresolved.waiting')}
        </span>
        <ChevronGlyph className={clsx(styles.chevron, expanded && styles.chevronOn)} />
      </button>
      <div id={panelId} className={clsx(styles.fold, !expanded && styles.shut)} inert={!expanded}>
        <div className={styles.foldInner}>
          {/* Mounted only while open, so a closed row reads nothing and holds no search. */}
          {expanded && (
            <DecisionPanel row={row} decisions={decisions} busy={busy} busyId={busyId} onLeave={onLeave} onAct={act} />
          )}
        </div>
      </div>
    </li>
  );
}

function DecisionPanel({
  row,
  decisions,
  busy,
  busyId,
  onLeave,
  onAct,
}: {
  readonly row: OutsideCatalogueRow;
  readonly decisions: DraftDecisions;
  readonly busy: boolean;
  readonly busyId: string;
  readonly onLeave: (() => void) | undefined;
  /** A decision is on its way from this panel: if it lands, focus goes to the row's header. */
  readonly onAct: (decision: Promise<boolean>) => void;
}) {
  const { t } = useTranslation();
  const { exercise, reopened, failure } = row;
  const name = exercise.name;
  const facts = exercise.proposedExercise === null ? null : proposalFacts(t, exercise.proposedExercise);
  const noSimilar = row.similar.length === 0;
  // One of Buddy's own picks opened to change: the trainer came to choose another exercise, so the
  // similar ones and the search lead, and nothing says „new exercise" until they look for one.
  const pickChange = onLeave !== undefined;
  const [searching, setSearching] = useState(false);
  const verifyButton = useRef<HTMLButtonElement>(null);
  const refuse = busy ? { 'aria-disabled': 'true' as const, 'aria-describedby': busyId } : {};
  const failureWords = failure === null ? null : addFailureWords(t, failure);
  const pick = (exerciseId: number, target: string) => {
    if (!busy) onAct(decisions.pick(name, exerciseId, target));
  };
  const { quickAdd, verify, handOver } = decisions;

  const proposal = (
    <>
      {!pickChange && <p className={styles.kicker}>{t('web:aiPlan.unresolved.kicker')}</p>}
      {/* Board D: the failure stands in the row it happened in, which stays open for the retry. The
        * region is always there, so the sentence is spoken when it arrives. */}
      <div className={clsx(styles.fold, styles.errorFold, failureWords === null && styles.shut)}>
        <div className={styles.foldInner}>
          <p className={styles.error} role="alert">
            {failureWords !== null && (
              <>
                <ErrorGlyph className={styles.errorGlyph} />
                <span>
                  <b>{failureWords.lead}</b> {failureWords.rest}
                </span>
              </>
            )}
          </p>
        </div>
      </div>
      {facts === null ? (
        !pickChange && <p className={styles.noProposal}>{t('web:aiPlan.unresolved.noProposal')}</p>
      ) : (
        <dl className={styles.facts}>
          {(['type', 'muscle', 'equipment', 'metrics'] as const).map((fact) => (
            <div key={fact} className={styles.fact}>
              <dt>{t(`web:aiPlan.unresolved.fact.${fact}`)}</dt>
              <dd>{facts[fact]}</dd>
            </div>
          ))}
        </dl>
      )}
      {facts !== null && row.nameTooLong && <p className={styles.noProposal}>{t('web:aiPlan.unresolved.nameTooLong', { max: MAX_EXERCISE_NAME_LENGTH })}</p>}
      {handOver !== null ? (
        // The builder's panel writes no catalogue: the name is decided on the Buddy page, its row open.
        <div className={clsx(styles.actions, styles.actionsOne)}>
          <button
            type="button"
            className={clsx(surfaces.button, styles.large)}
            onClick={() => {
              handOver(name);
            }}
          >
            {t('web:aiPlan.unresolved.handOver')}
          </button>
        </div>
      ) : (
        <div className={clsx(styles.actions, !(row.quickAddable && quickAdd !== null) && styles.actionsOne)}>
          {row.quickAddable && quickAdd !== null && (
            <button
              type="button"
              className={clsx(surfaces.button, surfaces.primary, styles.large)}
              {...refuse}
              onClick={() => {
                if (!busy) onAct(quickAdd([exercise]));
              }}
            >
              {decisions.adding(name) && <span className={clsx(surfaces.spinner, styles.busyGlyph)} aria-hidden="true" />}
              {t('web:aiPlan.unresolved.quickAdd')}
            </button>
          )}
          {verify !== null && (
            <button
              ref={verifyButton}
              type="button"
              className={clsx(surfaces.button, row.verifyLeads && surfaces.primary, styles.large)}
              {...refuse}
              onClick={() => {
                if (!busy) onAct(verify(exercise, verifyButton.current));
              }}
            >
              {t('web:aiPlan.unresolved.verify')}
            </button>
          )}
        </div>
      )}
    </>
  );

  const similar = (
    <>
      {/* Nothing similar to offer: no list and no heading for one, only the way into the catalogue. */}
      {!(noSimilar && searching) && (
        <div className={clsx(styles.similarHead, noSimilar && styles.similarHeadLinkOnly)}>
          {!noSimilar && <span>{t('web:aiPlan.unresolved.similar')}</span>}
          <button
            type="button"
            className={styles.link}
            onClick={() => {
              setSearching((current) => !current);
            }}
          >
            {searching ? t('web:aiPlan.unresolved.showSimilar') : t('web:aiPlan.unresolved.search')}
          </button>
        </div>
      )}
      {searching ? (
        <CatalogueSearch onPick={pick} busy={busy} busyId={busyId} takeFocus={noSimilar} />
      ) : (
        !noSimilar && (
          <ChoiceList
            choices={row.similar.map((candidate) => ({ id: candidate.exerciseId, name: candidate.name, note: candidate.difference }))}
            busy={busy}
            busyId={busyId}
            onPick={pick}
          />
        )
      )}
    </>
  );

  return (
    <div
      className={styles.panel}
      aria-busy={busy ? 'true' : undefined}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || onLeave === undefined) return;
        // Only this card closes: a panel around it stays open.
        event.stopPropagation();
        if (!busy) onLeave();
      }}
    >
      {pickChange ? (
        <>
          {similar}
          {proposal}
        </>
      ) : (
        <>
          {proposal}
          {similar}
        </>
      )}
      <div className={styles.foot}>
        <div className={styles.footActs}>
          <button
            type="button"
            className={clsx(surfaces.button, surfaces.small, surfaces.ghost, styles.skip)}
            {...refuse}
            onClick={() => {
              if (!busy) onAct(decisions.skip(name));
            }}
          >
            {t('web:aiPlan.unresolved.skip')}
          </button>
          {(reopened || pickChange) && (
            <button
              type="button"
              className={clsx(surfaces.button, surfaces.small)}
              {...refuse}
              onClick={() => {
                if (busy) return;
                if (onLeave !== undefined && !reopened) onLeave();
                else {
                  // Nothing is sent: the decision that stood stands, and the row folds at once.
                  onAct(Promise.resolve(true));
                  decisions.keep(name);
                }
              }}
            >
              {pickChange && !reopened ? t('web:aiPlan.assumptions.keepPick') : t('web:aiPlan.unresolved.keep')}
            </button>
          )}
        </div>
        <p className={styles.small}>{t('web:aiPlan.unresolved.remember')}</p>
      </div>
      <span id={busyId} className={hidden.srOnly}>
        {decisions.adding(name) ? t('web:aiPlan.unresolved.adding') : busy ? t('web:aiPlan.unresolved.saving') : ''}
      </span>
    </div>
  );
}

/** Exercises of the catalogue, one click each; nothing is chosen until one is pressed. */
function ChoiceList({
  choices,
  busy,
  busyId,
  onPick,
}: {
  /** `note`: a line under the name saying how it differs from the written one, as plain text. */
  readonly choices: readonly { readonly id: number; readonly name: string; readonly note?: string | null }[];
  readonly busy: boolean;
  readonly busyId: string;
  readonly onPick: (exerciseId: number, name: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <ul className={styles.similar}>
      {choices.map((choice) => (
        <li key={choice.id}>
          <button
            type="button"
            className={styles.choice}
            aria-disabled={busy ? 'true' : undefined}
            aria-describedby={busy ? busyId : undefined}
            onClick={() => {
              onPick(choice.id, choice.name);
            }}
          >
            <span className={styles.choiceText}>
              <span className={styles.choiceName}>{choice.name}</span>
              {choice.note != null && <span className={styles.choiceNote}>{choice.note}</span>}
            </span>
            <span className={styles.choiceGo} aria-hidden="true">
              {t('web:aiPlan.unresolved.choose')}
            </span>
            <ChevronGlyph className={styles.choiceGlyph} />
          </button>
        </li>
      ))}
    </ul>
  );
}

/**
 * „Szukaj w katalogu": the trainer's whole catalogue, read and searched exactly as the builder's
 * library does it (both content languages, Polish folding), in the app's one search field, with the
 * board's few matches under it. The catalogue is read only once this is open.
 */
function CatalogueSearch({
  onPick,
  busy,
  busyId,
  takeFocus,
}: {
  readonly onPick: (exerciseId: number, name: string) => void;
  readonly busy: boolean;
  readonly busyId: string;
  /** The link that opened it is gone (nothing similar to go back to): focus moves into the field. */
  readonly takeFocus: boolean;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (takeFocus) input.current?.focus();
  }, [takeFocus]);
  const library = useExerciseLibraryQuery();
  const text = library.query.text.trim();
  const results = library.catalogue.results.slice(0, SEARCH_RESULTS);
  return (
    <>
      <SearchField
        size="fill"
        disabled={false}
        inputRef={input}
        value={library.query.text}
        onChange={library.setText}
        label={t('web:aiPlan.unresolved.search')}
        placeholder={t('web:aiPlan.unresolved.search')}
        clearLabel={t('web:exerciseLibrary.clearSearch')}
      />
      {text === '' ? (
        <p className={styles.searchNote}>{t('web:aiPlan.unresolved.searchIdle')}</p>
      ) : library.isPending ? (
        <p className={styles.searchNote}>{t('web:planBuilder.rail.loading')}</p>
      ) : library.isError ? (
        <p className={styles.searchNote}>
          {t('web:planBuilder.rail.error')}{' '}
          <button type="button" className={styles.link} onClick={library.retry}>
            {t('web:planBuilder.rail.retry')}
          </button>
        </p>
      ) : results.length === 0 ? (
        <p className={styles.searchNote}>{t('web:aiPlan.unresolved.searchEmpty')}</p>
      ) : (
        <ChoiceList choices={results} busy={busy} busyId={busyId} onPick={onPick} />
      )}
    </>
  );
}
